import {
  ColumnLayout,
  FormField,
  Header,
  Input,
  SpaceBetween,
} from '@cloudscape-design/components';
import parseTime from 'components/HMI/components/parseTime';
import PLCNumberEditor from 'features/plc/components/PLCNumberEditor';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

function toDisplayValue(value, type) {
  if (value === undefined || value === null) {
    return '';
  }

  if (type === 'TIME') {
    return parseTime(value);
  }

  return String(value);
}

export default function ConfigSection({
  configFields,
  configValues = {},
  errorTextByKey = {},
  warningTextByKey = {},
  editable = false,
}) {
  const sections = useMemo(() => {
    const sectionMap = new Map();

    configFields.forEach((field) => {
      let section = sectionMap.get(field.section);

      if (!section) {
        section = {
          header: field.section,
          ungrouped: [],
          groups: [],
          groupMap: new Map(),
        };
        sectionMap.set(field.section, section);
      }

      if (!field.group) {
        section.ungrouped.push(field);
        return;
      }

      let group = section.groupMap.get(field.group);

      if (!group) {
        group = {
          header: field.group,
          rows: [],
          columns: 1,
          subgroupRowMap: new Map(),
        };
        section.groupMap.set(field.group, group);
        section.groups.push(group);
      }

      if (!field.subgroup) {
        group.rows.push({ key: field.key, fields: [field] });
        return;
      }

      const rowKey = `${field.group}-${field.subgroup}`;
      let row = group.subgroupRowMap.get(rowKey);

      if (!row) {
        row = {
          key: rowKey,
          fields: [],
        };
        group.subgroupRowMap.set(rowKey, row);
        group.rows.push(row);
      }

      row.fields.push(field);
      group.columns = Math.max(group.columns, row.fields.length);
    });

    return Array.from(sectionMap.values(), (section) => ({
      header: section.header,
      ungrouped: section.ungrouped,
      groups: section.groups.map((group) => ({
        header: group.header,
        rows: group.rows,
        columns: group.columns,
      })),
    }));
  }, [configFields]);

  const renderField = (field) => (
    editable ? (
      <PLCNumberEditor
        key={field.key}
        field={field}
      />
    ) : (
      <FormField
        key={field.key}
        label={field.label}
        errorText={errorTextByKey[field.key] || null}
        warningText={warningTextByKey[field.key] || null}
      >
        <Input
          value={toDisplayValue(configValues[field.key], field.type)}
          readOnly
        />
      </FormField>
    )
  );

  return (
    <SpaceBetween size="m">
      {sections.map((section) => (
        <div key={section.header}>
          <Header variant="h3">{section.header}</Header>
          {section.ungrouped.length ? (
            <ColumnLayout columns={section.ungrouped.length} minColumnWidth={120}>
              {section.ungrouped.map(renderField)}
            </ColumnLayout>
          ) : null}
          {section.groups.length ? (
            <ColumnLayout columns={section.groups.length} variant="text-grid">
              {section.groups.map((group) => (
                <SpaceBetween key={group.header} size="xs">
                  <Header variant="h4">{group.header}</Header>
                  {group.rows.map((row) => (
                    <ColumnLayout
                      key={row.key}
                      columns={group.columns}
                    >
                      {row.fields.map(renderField)}
                    </ColumnLayout>
                  ))}
                </SpaceBetween>
              ))}
            </ColumnLayout>
          ) : null}
        </div>
      ))}
    </SpaceBetween>
  );
}

ConfigSection.propTypes = {
  configFields: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    label: PropTypes.string.isRequired,
    max: PropTypes.number,
    min: PropTypes.number,
    section: PropTypes.string.isRequired,
    group: PropTypes.string,
    subgroup: PropTypes.string,
    type: PropTypes.string,
  })).isRequired,
  configValues: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
  ])),
  errorTextByKey: PropTypes.objectOf(PropTypes.string),
  warningTextByKey: PropTypes.objectOf(PropTypes.string),
  editable: PropTypes.bool,
};
